/**
 * JobCard Component
 * Displays job posting information in a card format
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Clock, 
  DollarSign, 
  Users, 
  Calendar,
  Briefcase,
  ChevronRight
} from 'lucide-react';

/**
 * JobCard Component
 * 
 * @param {Object} job - Job data
 * @param {function} onApply - Apply button handler
 * @param {boolean} hasApplied - Whether user has already applied
 * @returns {JSX.Element} JobCard component
 */
const JobCard = ({ job, onApply, hasApplied = false }) => {
  // Format salary range
  const formatSalary = (min, max, currency = 'ZAR') => {
    if (!min && !max) return 'Salary not specified';
    if (min && max) {
      return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    }
    return min ? `From ${currency} ${min.toLocaleString()}` : `Up to ${currency} ${max.toLocaleString()}`;
  };

  // Format posted date with better error handling
  const formatDate = (date) => {
    if (!date) return 'Date not specified';
    
    try {
      const now = new Date();
      let posted;
      
      // Handle different date formats
      if (date instanceof Date) {
        posted = date;
      } else if (typeof date === 'string' || typeof date === 'number') {
        posted = new Date(date);
      } else if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp
        posted = date.toDate();
      } else {
        return 'Date not specified';
      }
      
      // Check if date is valid
      if (isNaN(posted.getTime())) {
        return 'Date not specified';
      }
      
      const diffTime = Math.abs(now - posted);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not specified';
    }
  };

  // Format application deadline with better error handling
  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    
    try {
      let deadlineDate;
      
      // Handle different date formats
      if (deadline instanceof Date) {
        deadlineDate = deadline;
      } else if (typeof deadline === 'string' || typeof deadline === 'number') {
        deadlineDate = new Date(deadline);
      } else if (deadline.toDate && typeof deadline.toDate === 'function') {
        // Firestore Timestamp
        deadlineDate = deadline.toDate();
      } else {
        return null;
      }
      
      // Check if date is valid
      if (isNaN(deadlineDate.getTime())) {
        return null;
      }
      
      return deadlineDate.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting deadline:', error);
      return null;
    }
  };

  // Get job type badge color
  const getJobTypeBadge = (type) => {
    const colors = {
      'full-time': 'bg-green-100 text-green-800',
      'part-time': 'bg-blue-100 text-blue-800',
      'contract': 'bg-purple-100 text-purple-800',
      'internship': 'bg-orange-100 text-orange-800',
      'remote': 'bg-indigo-100 text-indigo-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Helper function to check if logo should be shown
  const hasValidLogo = (logo) => {
    return logo && 
           typeof logo === 'string' && 
           logo.trim() !== '' && 
           logo !== 'undefined' && 
           logo !== 'null' &&
           logo.startsWith('http'); // Ensure it's a valid URL
  };

  // Get company name safely
  const getCompanyName = () => {
    return job.company?.name || job.companyName || 'Unknown Company';
  };

  // Get first character for fallback
  const getCompanyInitial = () => {
    const companyName = getCompanyName();
    return companyName.charAt(0).toUpperCase();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Company Logo and Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 relative">
            {hasValidLogo(job.company?.logo) ? (
              <>
                <img 
                  src={job.company.logo} 
                  alt={getCompanyName()}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // If image fails to load, hide the img and show fallback
                    e.target.style.display = 'none';
                    e.target.parentNode.querySelector('.fallback-logo').style.display = 'flex';
                  }}
                />
                <div 
                  className="fallback-logo absolute inset-0 w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg"
                  style={{ display: 'none' }}
                >
                  {getCompanyInitial()}
                </div>
              </>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                {getCompanyInitial()}
              </div>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              <Link 
                to={`/career/jobs/${job.id}`}
                className="hover:text-blue-600 transition-colors"
              >
                {job.title}
              </Link>
            </h3>
            <p className="text-sm text-gray-600">{getCompanyName()}</p>
          </div>
        </div>

        {/* Job Type Badge */}
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getJobTypeBadge(job.jobType)}`}>
          {job.jobType ? job.jobType.charAt(0).toUpperCase() + job.jobType.slice(1) : 'Not specified'}
        </span>
      </div>

      {/* Job Details */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-600">
        <div className="flex items-center">
          <MapPin size={16} className="mr-2 text-gray-400" />
          <span>{job.location || 'Location not specified'}</span>
        </div>
        <div className="flex items-center">
          <Briefcase size={16} className="mr-2 text-gray-400" />
          <span>{job.experienceLevel || 'Not specified'}</span>
        </div>
        <div className="flex items-center">
          <DollarSign size={16} className="mr-2 text-gray-400" />
          <span>{formatSalary(job.salary?.min, job.salary?.max, job.salary?.currency)}</span>
        </div>
        <div className="flex items-center">
          <Calendar size={16} className="mr-2 text-gray-400" />
          <span>{formatDate(job.createdAt)}</span>
        </div>
      </div>

      {/* Job Description Preview */}
      <p className="text-gray-700 text-sm mb-4 line-clamp-2">
        {job.description || 'No description available'}
      </p>

      {/* Skills Tags */}
      {job.requirements?.skills && job.requirements.skills.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {job.requirements.skills.slice(0, 4).map((skill, index) => (
              <span 
                key={index}
                className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
              >
                {skill}
              </span>
            ))}
            {job.requirements.skills.length > 4 && (
              <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                +{job.requirements.skills.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center text-sm text-gray-500">
          <Users size={16} className="mr-1" />
          <span>
            {job.applicationCount?.length || 0} applicant{job.applicationCount?.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center space-x-3">
          <Link
            to={`/career/jobs/${job.id}`}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
          >
            View Details
            <ChevronRight size={16} className="ml-1" />
          </Link>
          
          {hasApplied ? (
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm font-medium">
              Applied
            </span>
          ) : (
            <button
              onClick={() => onApply && onApply(job)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
            >
              Apply Now
            </button>
          )}
        </div>
      </div>

      {/* Urgency Indicator */}
      {job.isUrgent && (
        <div className="mt-3 flex items-center text-red-600 text-sm">
          <Clock size={16} className="mr-1" />
          <span className="font-medium">Urgent hiring</span>
        </div>
      )}

      {/* Application Deadline */}
      {formatDeadline(job.applicationDeadline) && (
        <div className="mt-2 text-xs text-gray-500">
          Application deadline: {formatDeadline(job.applicationDeadline)}
        </div>
      )}
    </div>
  );
};

export default JobCard;